import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowRight, Sparkles, Search, Star, X,
  MapPin, Check, ChevronRight, Zap, Layers,
  Brain, Bell, BookMarked, BarChart3, Users,
  GitCompare, SlidersHorizontal, TrendingUp, GripVertical, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useInView } from '../hooks/useInView';
import { SEO, schemaOrganization, schemaWebSite, schemaSoftwareApp } from '../components/SEO';

// ── Font shorthand ────────────────────────────────────────────────────────────
const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

// ── Scroll-reveal wrapper ─────────────────────────────────────────────────────
const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${className}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(18px)',
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ── Mono label ────────────────────────────────────────────────────────────────
const MonoLabel = ({ children }) => (
  <span
    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground border border-border px-3 py-1.5"
    style={{ fontFamily: MONO }}
  >
    <span className="w-1.5 h-1.5 bg-[hsl(var(--primary))] inline-block flex-shrink-0" />
    {children}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// AI ANALYST DEMO — auto-playing "Ask AI about a candidate" preview
// ─────────────────────────────────────────────────────────────────────────────
const ANALYST_SCRIPT = [
  { role: 'assistant', text: 'Sofia Romano is a Senior Womenswear Designer in Milan with 8 years across luxury RTW houses, scoring 92/100 on brief fit. Her aesthetic signature leans quiet-luxury and tailored minimalism, backed by CLO 3D mastery and immediate availability — a strong fit for a contemporary luxury studio.' },
  { role: 'user', text: 'Is she ready to lead a small design team?' },
  { role: 'assistant', text: 'Yes — her authority score (7.8/10) and progression from designer to lead roles at two Milanese houses signal team-ready seniority, though she has not held a formal "Head of" title yet.' },
  { role: 'user', text: 'How strong is her sustainable design experience?' },
  { role: 'assistant', text: 'Moderate. Her portfolio tags include responsible sourcing and her cultural DNA shows circular-fashion references, but sustainability is a secondary theme rather than her core specialism.' },
];

const AIAnalystMock = ({ inView }) => {
  const [visibleCount, setVisibleCount] = useState(0);   // fully shown messages
  const [typed, setTyped] = useState('');                // chars of current assistant msg
  const timers = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setVisibleCount(0); setTyped('');

    let cancelled = false;
    const run = async () => {
      const wait = (ms) => new Promise(res => { const t = setTimeout(res, ms); timers.current.push(t); });
      await wait(500);
      for (let i = 0; i < ANALYST_SCRIPT.length; i++) {
        if (cancelled) return;
        const msg = ANALYST_SCRIPT[i];
        if (msg.role === 'user') {
          setVisibleCount(i + 1);
          await wait(850);
        } else {
          for (let c = 1; c <= msg.text.length; c++) {
            if (cancelled) return;
            setTyped(msg.text.slice(0, c));
            await wait(11);
          }
          setVisibleCount(i + 1);
          setTyped('');
          await wait(1000);
        }
      }
    };
    run();
    return () => { cancelled = true; timers.current.forEach(clearTimeout); };
  }, [inView]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleCount, typed]);

  const typingIndex = visibleCount;
  const isTyping = typingIndex < ANALYST_SCRIPT.length && ANALYST_SCRIPT[typingIndex]?.role === 'assistant' && typed.length > 0;

  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] relative overflow-hidden" data-testid="ai-analyst-demo-card">
      <div
        className="absolute top-5 right-5 lg:top-7 lg:right-7"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 200ms', animation: inView ? 'floatCard 3s ease-in-out infinite' : 'none' }}
      >
        <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
      </div>

      {/* Candidate header card */}
      <div
        className="bg-white border border-border p-4 mb-3 flex items-center gap-3"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 100ms' }}
      >
        <div className="w-10 h-10 bg-foreground text-background flex items-center justify-center flex-shrink-0" style={{ fontFamily: SERIF, fontSize: '0.9rem' }}>SR</div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">Sofia Romano</p>
          <p className="text-xs text-muted-foreground truncate" style={{ fontFamily: MONO, fontSize: '0.7rem' }}>Sr. Womenswear Designer · Milan</p>
        </div>
        <div className="ml-auto border border-[hsl(var(--primary)/0.4)] px-2.5 py-1.5 text-center flex-shrink-0" style={{ background: 'hsl(var(--primary)/0.04)' }}>
          <p className="text-lg font-bold leading-none text-[hsl(var(--primary))]" style={{ fontFamily: SERIF }}>92</p>
          <p className="text-muted-foreground" style={{ fontFamily: MONO, fontSize: '0.55rem' }}>FIT / 100</p>
        </div>
      </div>

      {/* Conversation card */}
      <div
        className="bg-white border border-border p-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 320ms' }}
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5" style={{ fontFamily: MONO, fontSize: '0.6rem' }}>
          <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" strokeWidth={1.5} />
          AI Analyst — Profile Snapshot
        </p>

        <div ref={scrollRef} className="space-y-3.5 min-h-[230px] max-h-[230px] overflow-hidden">
          {ANALYST_SCRIPT.slice(0, visibleCount).map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="bg-foreground text-background px-3 py-1.5 max-w-[88%] text-xs leading-relaxed">{m.text}</div>
              </div>
            ) : i === 0 ? (
              <p key={i} className="text-foreground leading-relaxed italic border-l-2 border-[hsl(var(--primary))] pl-3" style={{ fontSize: '0.82rem' }}>
                {m.text}
              </p>
            ) : (
              <div key={i} className="flex items-start gap-2 max-w-[92%]">
                <Sparkles className="w-3 h-3 text-[hsl(var(--primary))] mt-1 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-foreground leading-relaxed" style={{ fontSize: '0.78rem' }}>{m.text}</p>
              </div>
            )
          ))}

          {/* Currently typing assistant message */}
          {isTyping && (
            typingIndex === 0 ? (
              <p className="text-foreground leading-relaxed italic border-l-2 border-[hsl(var(--primary))] pl-3" style={{ fontSize: '0.82rem' }}>
                {typed}<span className="inline-block w-0.5 h-3.5 bg-[hsl(var(--primary))] ml-0.5 align-middle animate-pulse" />
              </p>
            ) : (
              <div className="flex items-start gap-2 max-w-[92%]">
                <Sparkles className="w-3 h-3 text-[hsl(var(--primary))] mt-1 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-foreground leading-relaxed" style={{ fontSize: '0.78rem' }}>
                  {typed}<span className="inline-block w-0.5 h-3.5 bg-[hsl(var(--primary))] ml-0.5 align-middle animate-pulse" />
                </p>
              </div>
            )
          )}
        </div>

        {/* Input affordance */}
        <div className="flex items-center gap-2 border border-border mt-3 px-3 py-2">
          <span className="flex-1 text-muted-foreground/60" style={{ fontFamily: MONO, fontSize: '0.7rem' }}>
            Ask anything about Sofia…
          </span>
          <Send className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MATCHMAKER POPUP — animated real-time preview
// ─────────────────────────────────────────────────────────────────────────────
const DEMO_CANDIDATES = [
  { initials: 'SR', name: 'Sofia Romano',   role: 'Sr. Womenswear Designer', city: 'Milan',  score: 92, skills: 23, style: 38, social: 18, logistics: 13, why: '"Luxury RTW expertise + CLO 3D mastery + immediate availability aligns perfectly."' },
  { initials: 'LB', name: 'Luca Bernier',   role: 'Womenswear Designer',     city: 'Paris',  score: 84, skills: 21, style: 34, social: 16, logistics: 13, why: '"Strong Parisian luxury background, haute couture training matches brief DNA."' },
  { initials: 'AT', name: 'Aiko Tanaka',    role: 'Sr. Creative Designer',   city: 'Tokyo',  score: 78, skills: 20, style: 30, social: 17, logistics: 11, why: '"Avant-garde style signature with verified digital-first portfolio performance."' },
];

const MatchmakerPopup = ({ onClose, onTry }) => {
  const [phase, setPhase] = useState(0);
  // phase 0: typing JD | 1: analyzing | 2: results | 3: explanation visible
  const [typedChars, setTypedChars] = useState(0);
  const [visibleCands, setVisibleCands] = useState(0);
  const [barWidths, setBarWidths] = useState([0, 0, 0, 0]);
  const timerRef = useRef([]);

  const JD_TEXT = 'Sr. Creative Director · Luxury RTW · Paris · 8+ yrs · CLO 3D · Immediate';

  const clear = () => timerRef.current.forEach(clearTimeout);

  const startAnimation = useCallback(() => {
    clear();
    setPhase(0); setTypedChars(0); setVisibleCands(0); setBarWidths([0, 0, 0, 0]);

    // Phase 0: type the JD character by character
    let charIdx = 0;
    const typeInterval = setInterval(() => {
      charIdx++;
      setTypedChars(charIdx);
      if (charIdx >= JD_TEXT.length) {
        clearInterval(typeInterval);
        // Phase 1: analyzing
        const t1 = setTimeout(() => setPhase(1), 200);
        // Phase 2: show results
        const t2 = setTimeout(() => {
          setPhase(2);
          // Reveal candidates one by one
          [0, 1, 2].forEach(i => {
            const tc = setTimeout(() => setVisibleCands(v => v + 1), i * 350);
            timerRef.current.push(tc);
          });
          // Animate score bars
          const tb = setTimeout(() => setBarWidths([92, 84, 78, 0]), 600);
          timerRef.current.push(tb);
        }, 1600);
        // Phase 3: explanation
        const t3 = setTimeout(() => setPhase(3), 3200);
        timerRef.current.push(t1, t2, t3);
      }
    }, 42);
    timerRef.current.push(typeInterval);
  }, []);

  useEffect(() => {
    startAnimation();
    return clear;
  }, [startAnimation]);

  // Score bar animation helper
  const scoreToBar = (score) => ({ skills: 25, style: 40, social: 20, logistics: 15 });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-8"
      style={{ background: 'rgba(10,10,10,0.82)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-3xl bg-white border border-border overflow-hidden"
        style={{
          animation: 'popupIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        data-testid="matchmaker-popup"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-[hsl(var(--secondary))]">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-[hsl(var(--primary))] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold tracking-wide">AI Matchmaker — Live Preview</span>
            <span
              className="flex items-center gap-1.5 text-xs px-2 py-0.5 border"
              style={{
                fontFamily: MONO,
                borderColor: phase >= 2 ? 'hsl(var(--primary) / 0.5)' : 'hsl(var(--border))',
                color: phase >= 2 ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                background: phase >= 2 ? 'hsl(var(--primary) / 0.06)' : 'transparent',
                transition: 'all 400ms',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: phase >= 1 ? 'hsl(var(--primary))' : '#aaa',
                  animation: phase === 1 ? 'blink 0.8s step-end infinite' : 'none',
                }}
              />
              {phase === 0 ? 'INPUTTING' : phase === 1 ? 'ANALYZING' : 'RESULTS READY'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            data-testid="matchmaker-popup-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* JD Input */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO }}>
              Job Description Input
            </p>
            <div className="border border-border bg-[hsl(var(--secondary))] px-4 py-3 flex items-center gap-2 min-h-[48px]">
              <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-sm flex-1" style={{ fontFamily: MONO, fontSize: '0.8rem' }}>
                {JD_TEXT.slice(0, typedChars)}
                {typedChars < JD_TEXT.length && (
                  <span className="inline-block w-0.5 h-3.5 bg-foreground ml-0.5 align-middle" style={{ animation: 'blink 0.6s step-end infinite' }} />
                )}
              </span>
              {typedChars >= JD_TEXT.length && phase >= 2 && (
                <span className="text-xs px-2 py-0.5 bg-foreground text-background flex-shrink-0" style={{ fontFamily: MONO }}>
                  52 scored
                </span>
              )}
            </div>
          </div>

          {/* Analyzing state */}
          {phase === 1 && (
            <div className="border border-border px-4 py-5 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className="w-1 bg-[hsl(var(--primary))]"
                      style={{
                        height: `${12 + Math.sin(i) * 8}px`,
                        animation: `blink ${0.6 + i * 0.1}s ease-in-out infinite alternate`,
                        animationDelay: `${i * 80}ms`,
                      }}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground" style={{ fontFamily: MONO }}>
                  Parsing semantic vectors...
                </span>
              </div>
              <div className="w-full h-1 bg-secondary overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--primary))]"
                  style={{ animation: 'progress 1.4s ease-out forwards' }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {phase >= 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>
                  Top Matches — Ranked by Overall Fit
                </p>
                <span className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>52 candidates scored</span>
              </div>

              {DEMO_CANDIDATES.map((c, i) => (
                <div
                  key={c.name}
                  className="border border-border bg-white p-4"
                  style={{
                    opacity: i < visibleCands ? 1 : 0,
                    transform: i < visibleCands ? 'translateY(0)' : 'translateY(10px)',
                    transition: `opacity 400ms ease-out, transform 400ms ease-out`,
                  }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 bg-secondary border border-border flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">
                      {c.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{c.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        {c.role}
                        <span className="opacity-40">·</span>
                        <MapPin className="w-2.5 h-2.5" />{c.city}
                      </p>
                    </div>
                    <div className="border border-border px-3 py-1.5 text-center flex-shrink-0" style={{ borderColor: i === 0 ? 'hsl(var(--primary) / 0.5)' : '' }}>
                      <p className="text-xl font-bold leading-none" style={{ fontFamily: SERIF, color: i === 0 ? 'hsl(var(--primary))' : '' }}>{c.score}</p>
                      <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>/ 100</p>
                    </div>
                  </div>

                  {/* Score bars */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { l: 'Skills',     v: c.skills, max: 25, col: 'bg-blue-400' },
                      { l: 'Style DNA',  v: c.style,  max: 40, col: 'bg-[hsl(var(--primary))]' },
                      { l: 'Social',     v: c.social, max: 20, col: 'bg-green-400' },
                      { l: 'Logistics',  v: c.logistics, max: 15, col: 'bg-gray-400' },
                    ].map(b => (
                      <div key={b.l}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground truncate" style={{ fontFamily: MONO, fontSize: '0.65rem' }}>{b.l}</span>
                          <span className="font-medium text-muted-foreground" style={{ fontFamily: MONO, fontSize: '0.65rem' }}>{Math.round((b.v / b.max) * 100)}%</span>
                        </div>
                        <div className="h-1 bg-secondary overflow-hidden">
                          <div
                            className={`h-full ${b.col}`}
                            style={{
                              width: barWidths[0] > 0 ? `${(b.v / b.max) * 100}%` : '0%',
                              transition: `width 700ms ease-out ${600 + i * 150}ms`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* AI explanation */}
                  {phase >= 3 && i === 0 && (
                    <p
                      className="text-xs text-muted-foreground italic border-l-2 border-[hsl(var(--primary))] pl-2.5 leading-relaxed"
                      style={{ opacity: phase >= 3 ? 1 : 0, transition: 'opacity 600ms ease-out 200ms' }}
                    >
                      {c.why}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          {phase >= 3 && (
            <div
              className="border-t border-border pt-5 flex items-center justify-between gap-4"
              style={{ opacity: phase >= 3 ? 1 : 0, transition: 'opacity 600ms ease-out 400ms' }}
            >
              <p className="text-sm text-muted-foreground max-w-sm">
                Run this against your actual job description with 52 real candidates.
              </p>
              <div className="flex gap-2.5 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startAnimation}
                  className="border-border text-muted-foreground hover:border-foreground text-xs gap-1.5"
                  data-testid="matchmaker-popup-replay"
                >
                  Replay demo
                </Button>
                <Button
                  size="sm"
                  onClick={onTry}
                  className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.88)] text-white border-0 text-xs gap-1.5"
                  data-testid="matchmaker-popup-try"
                >
                  Try for real <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WORKFLOW POPUP — animated pipeline preview
// ═══════════════════════════════════════════════════════════════════════════════
const PIPELINE_STAGES = ['Saved', 'Contacted', 'In Process', 'Hired'];
const GALLERY_CARDS = [
  { initials: 'SR', name: 'Sofia Romano',   role: 'Sr. Womenswear', city: 'Milan',  rank: 9.9 },
  { initials: 'LB', name: 'Luca Bernier',   role: 'Art Director',   city: 'Paris',  rank: 9.7 },
  { initials: 'AT', name: 'Aiko Tanaka',    role: 'Photographer',   city: 'Tokyo',  rank: 9.5 },
];

const WorkflowPopup = ({ onClose, onTry }) => {
  const [phase, setPhase] = useState(0);
  // 0: gallery view | 1: drag animation | 2: card in pipeline | 3: move stage | 4: hired
  const [dragCard, setDragCard] = useState(null);
  const [pipelineCards, setPipelineCards] = useState({ saved: [], contacted: ['Marco V.'], in_process: ['Elise D.'], hired: ['Nina K.'] });
  const [notif, setNotif] = useState(false);
  const timerRef = useRef([]);

  const clear = () => timerRef.current.forEach(clearTimeout);

  const startAnimation = useCallback(() => {
    clear();
    setPhase(0); setDragCard(null); setNotif(false);
    setPipelineCards({ saved: [], contacted: ['Marco V.'], in_process: ['Elise D.'], hired: ['Nina K.'] });

    const t1 = setTimeout(() => { setPhase(1); setDragCard(GALLERY_CARDS[0]); }, 1500);
    const t2 = setTimeout(() => {
      setPhase(2);
      setPipelineCards(p => ({ ...p, saved: ['Sofia R.'] }));
      setDragCard(null);
    }, 3200);
    const t3 = setTimeout(() => setPhase(3), 4800);
    const t4 = setTimeout(() => {
      setPipelineCards(p => ({ saved: [], contacted: ['Marco V.', 'Sofia R.'], in_process: ['Elise D.'], hired: ['Nina K.'] }));
    }, 5200);
    const t5 = setTimeout(() => setPhase(4), 6500);
    const t6 = setTimeout(() => {
      setPipelineCards(p => ({ saved: [], contacted: ['Marco V.'], in_process: ['Elise D.', 'Sofia R.'], hired: ['Nina K.'] }));
    }, 6900);
    const t7 = setTimeout(() => setNotif(true), 7800);
    timerRef.current.push(t1, t2, t3, t4, t5, t6, t7);
  }, []);

  useEffect(() => { startAnimation(); return clear; }, [startAnimation]);

  const stageColors = { saved: 'bg-white', contacted: 'bg-blue-50', in_process: 'bg-amber-50', hired: 'bg-green-50' };
  const stageKeys = ['saved', 'contacted', 'in_process', 'hired'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-8"
      style={{ background: 'rgba(10,10,10,0.82)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-3xl bg-white border border-border overflow-hidden"
        style={{ animation: 'popupIn 0.35s cubic-bezier(0.16,1,0.3,1) both', maxHeight: '90vh', overflowY: 'auto' }}
        data-testid="workflow-popup"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-[hsl(var(--secondary))]">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-foreground flex items-center justify-center">
              <GripVertical className="w-3.5 h-3.5 text-background" />
            </div>
            <span className="text-sm font-semibold tracking-wide">Pipeline Workflow — Live Preview</span>
            <span
              className="flex items-center gap-1.5 text-xs px-2 py-0.5 border"
              style={{ fontFamily: MONO }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{ animation: 'blink 1.2s step-end infinite' }} />
              {phase === 0 ? 'GALLERY' : phase <= 2 ? 'DRAGGING' : phase <= 4 ? 'MOVING STAGES' : 'ACTIVE'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted text-muted-foreground transition-colors" data-testid="workflow-popup-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Gallery strip */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO }}>
              Step 1 — Find talent in the Gallery
            </p>
            <div className="border border-border bg-[hsl(var(--secondary))] p-3 flex gap-2.5 overflow-x-auto">
              {GALLERY_CARDS.map((c, i) => (
                <div
                  key={c.name}
                  className="flex-shrink-0 bg-white border border-border p-2.5 flex items-center gap-2.5 min-w-[180px]"
                  style={{
                    borderColor: phase === 1 && i === 0 ? 'hsl(var(--primary))' : '',
                    transform: phase === 1 && i === 0 ? 'scale(0.97)' : 'scale(1)',
                    transition: 'all 300ms ease-out',
                    opacity: phase >= 2 && i === 0 ? 0.4 : 1,
                  }}
                >
                  <div className="w-7 h-7 bg-secondary border border-border flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">
                    {c.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.role}</p>
                  </div>
                  <div className="text-xs font-bold flex-shrink-0" style={{ fontFamily: MONO }}>{c.rank}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Drag arrow indicator */}
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            style={{ fontFamily: MONO, opacity: phase >= 1 && phase < 3 ? 1 : 0.3, transition: 'opacity 400ms' }}
          >
            <div className="flex-1 h-px bg-border" />
            <span>Drag candidate to pipeline stage</span>
            <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Kanban pipeline */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO }}>
              Step 2 — Manage in Kanban Pipeline
            </p>
            <div className="grid grid-cols-4 gap-2">
              {stageKeys.map((key, si) => (
                <div
                  key={key}
                  className={`${stageColors[key]} border border-border p-2.5 min-h-[120px]`}
                  style={{
                    borderColor: phase === 2 && key === 'saved' ? 'hsl(var(--primary) / 0.5)' : '',
                    boxShadow: phase === 2 && key === 'saved' ? '0 0 0 2px hsl(var(--primary) / 0.15)' : '',
                    transition: 'all 300ms',
                  }}
                >
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO, fontSize: '0.6rem' }}>
                    {PIPELINE_STAGES[si]}
                  </p>
                  <div className="space-y-1.5">
                    {(pipelineCards[key] || []).map((name) => (
                      <div
                        key={name}
                        className="bg-white border border-border px-2 py-1.5 text-xs font-medium"
                        style={{
                          animation: name === 'Sofia R.' && phase >= 2 ? 'popupIn 0.3s ease-out' : 'none',
                          borderColor: name === 'Sofia R.' ? 'hsl(var(--primary) / 0.4)' : '',
                        }}
                      >
                        {name}
                        {name === 'Sofia R.' && <span className="ml-1.5 text-[hsl(var(--primary))]" style={{ fontSize: '0.6rem', fontFamily: MONO }}>NEW</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notification */}
          <div
            className="flex items-center gap-3 border border-border px-4 py-3 bg-[hsl(var(--secondary))]"
            style={{ opacity: notif ? 1 : 0, transform: notif ? 'translateY(0)' : 'translateY(6px)', transition: 'all 500ms ease-out' }}
          >
            <Bell className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold">Sofia Romano updated her availability</p>
              <p className="text-xs text-muted-foreground">Immediate → 1 month notice · Milan, Italy</p>
            </div>
            <span className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full flex-shrink-0" />
          </div>

          {/* CTA */}
          <div className="border-t border-border pt-5 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground max-w-sm">
              Your full pipeline, Kanban stages, drag-and-drop, notes, and notifications — all in one place.
            </p>
            <div className="flex gap-2.5 flex-shrink-0">
              <button
                onClick={startAnimation}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs border border-border text-muted-foreground hover:border-foreground transition-all"
                data-testid="workflow-popup-replay"
              >
                Replay
              </button>
              <button
                onClick={onTry}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs text-white"
                style={{ background: '#1c2128' }}
                data-testid="workflow-popup-try"
              >
                Open Pipeline <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ANIMATED MOCK FRAMES
// ═══════════════════════════════════════════════════════════════════════════════

// Mock 1: Gallery candidate cards
const GalleryMock = ({ inView }) => {
  const cards = [
    { initials: 'SR', name: 'Sofia Romano',    role: 'Senior Womenswear', city: 'Milan',   rank: 9.9, delay: 0   },
    { initials: 'LB', name: 'Luca Bernier',    role: 'Art Director',       city: 'Paris',   rank: 9.7, delay: 120 },
    { initials: 'AT', name: 'Aiko Tanaka',     role: 'Fashion Photographer', city: 'Tokyo', rank: 9.5, delay: 240 },
  ];
  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, hsl(220 13% 18%) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, hsl(220 13% 18%) 40px)',
      }} />

      {/* Filter strip */}
      <div
        className="flex gap-2 flex-wrap mb-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 600ms ease-out 60ms' }}
      >
        {['Role: Womenswear ×', 'City: Milan ×', 'Senior ×', 'Available ×'].map(chip => (
          <span key={chip} className="text-xs border border-foreground/25 px-2.5 py-0.5 text-muted-foreground" style={{ fontFamily: MONO, fontSize: '0.65rem' }}>
            {chip}
          </span>
        ))}
        <span className="text-xs ml-auto text-muted-foreground" style={{ fontFamily: MONO, fontSize: '0.65rem' }}>results</span>
      </div>

      {/* Cards */}
      <div className="space-y-2.5">
        {cards.map((c, i) => (
          <div
            key={c.name}
            className="bg-white border border-border p-3.5 flex items-center gap-3.5"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(10px)',
              transition: `opacity 600ms ease-out ${120 + c.delay}ms, transform 600ms ease-out ${120 + c.delay}ms`,
              animation: inView ? `floatCard ${2.8 + i * 0.5}s ease-in-out infinite` : 'none',
              animationDelay: `${i * 0.7}s`,
            }}
          >
            <div className="w-9 h-9 bg-secondary border border-border flex-shrink-0 flex items-center justify-center text-xs font-bold text-muted-foreground">
              {c.initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{c.name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>{c.role}</span>
                <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{c.city}</span>
              </div>
            </div>
            {/* Score bars */}
            <div className="flex-shrink-0 w-24 hidden sm:block">
              <div className="grid grid-cols-4 gap-0.5">
                {[
                  { w: '88%', c: 'bg-blue-400' },
                  { w: '92%', c: 'bg-[hsl(var(--primary))]' },
                  { w: '80%', c: 'bg-green-400' },
                  { w: '86%', c: 'bg-gray-400' },
                ].map((b, bi) => (
                  <div key={bi} className="space-y-1">
                    <div className="h-8 bg-secondary overflow-hidden flex items-end">
                      <div className={`w-full ${b.c}`} style={{
                        height: inView ? b.w : '0%',
                        transition: `height 700ms ease-out ${500 + bi * 100 + c.delay}ms`,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 border border-border px-2 py-1 text-center">
              <p className="text-sm font-bold leading-none">{c.rank}</p>
              <p className="text-xs text-muted-foreground leading-none mt-0.5" style={{ fontFamily: MONO }}>rank</p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom action strip */}
      <div
        className="flex items-center justify-between mt-5 pt-4 border-t border-border"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 600ms ease-out 580ms' }}
      >
        <span className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>AI TALENT BRIEF active</span>
        <span className="text-xs bg-foreground text-background px-3 py-1" style={{ fontFamily: MONO }}>
          Add all to pipeline →
        </span>
      </div>
    </div>
  );
};

// Mock 2: AI Matchmaker
const MatchmakerMock = ({ inView }) => {
  const scores = [
    { label: 'Hard Skills',    w: '88%', color: 'bg-blue-400',              delay: 550 },
    { label: 'Semantic Style', w: '90%', color: 'bg-[hsl(var(--primary))]', delay: 700 },
    { label: 'Social Proof',   w: '85%', color: 'bg-green-400',             delay: 850 },
    { label: 'Logistics',      w: '80%', color: 'bg-gray-400',              delay: 1000 },
  ];
  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] relative overflow-hidden">
      <div
        className="absolute top-4 right-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 200ms', animation: inView ? 'floatCard 3s ease-in-out infinite' : 'none' }}
      >
        <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
      </div>

      {/* JD input */}
      <div
        className="bg-white border border-border p-4 mb-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 100ms' }}
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO }}>
          Job Description → Parsed
        </p>
        <div className="flex items-center gap-2">
          <p
            className="text-sm text-foreground overflow-hidden whitespace-nowrap flex-1"
            style={{
              animation: inView ? 'typing 3.2s steps(52, end) forwards' : 'none',
              width: inView ? undefined : '0ch',
              fontFamily: MONO,
              fontSize: '0.78rem',
            }}
          >
            Sr. Womenswear Designer · Milan · 6+ yrs luxury RTW…
          </p>
          <span
            className="inline-block w-0.5 h-4 bg-foreground flex-shrink-0"
            style={{ animation: inView ? 'blink 0.7s step-end infinite' : 'none' }}
          />
        </div>
      </div>

      {/* Score card */}
      <div
        className="bg-white border border-border p-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 320ms' }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-sm font-semibold">Sofia Romano</p>
            <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>Senior Womenswear · Milan · 8 yrs</p>
          </div>
          <div className="border border-[hsl(var(--primary)/0.4)] px-3 py-2 text-center flex-shrink-0" style={{ background: 'hsl(var(--primary)/0.04)' }}>
            <p className="text-2xl font-bold leading-none text-[hsl(var(--primary))]" style={{ fontFamily: SERIF }}>87</p>
            <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: MONO }}>/ 100</p>
          </div>
        </div>

        <div className="space-y-2.5">
          {scores.map(s => (
            <div key={s.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground" style={{ fontFamily: MONO, fontSize: '0.7rem' }}>{s.label}</span>
                <span className="font-medium" style={{ fontFamily: MONO, fontSize: '0.7rem' }}>{s.w}</span>
              </div>
              <div className="h-1.5 bg-secondary overflow-hidden">
                <div
                  className={`h-full ${s.color}`}
                  style={{ width: inView ? s.w : '0%', transition: `width 700ms ease-out ${s.delay}ms` }}
                />
              </div>
            </div>
          ))}
        </div>

        <p
          className="text-xs text-muted-foreground italic border-l-2 border-[hsl(var(--primary))] pl-2.5 mt-4 leading-relaxed"
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 1300ms' }}
        >
          "Strong CLO 3D + luxury RTW background aligns perfectly. Logistical score boosted by immediate availability and Milan location match."
        </p>
      </div>
    </div>
  );
};

// Mock 3: Pipeline Kanban
const PipelineMock = ({ inView }) => {
  const lanes = [
    { label: 'Saved',       bg: 'bg-white',    cards: ['Sofia R.', 'Luca B.'],   drift: false },
    { label: 'Contacted',   bg: 'bg-blue-50',  cards: ['Aiko T.'],               drift: true  },
    { label: 'In Process',  bg: 'bg-amber-50', cards: ['Marco V.', 'Elise D.'], drift: false },
    { label: 'Hired',       bg: 'bg-green-50', cards: ['Nina K.'],               drift: false },
  ];
  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] overflow-hidden">
      <div className="grid grid-cols-4 gap-2 mb-4">
        {lanes.map((lane, li) => (
          <div
            key={lane.label}
            className={`${lane.bg} border border-border p-2.5`}
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(14px)',
              transition: `opacity 600ms ease-out ${li * 90}ms, transform 600ms ease-out ${li * 90}ms`,
            }}
          >
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ fontFamily: MONO, fontSize: '0.6rem' }}>
              {lane.label}
            </p>
            <div className="space-y-1.5">
              {lane.cards.map((name, ci) => (
                <div
                  key={name}
                  className="bg-white border border-border px-2 py-1.5 text-xs font-medium"
                  style={{
                    animation: lane.drift && inView ? `driftX ${3.5 + ci * 0.8}s ease-in-out infinite` : 'none',
                    animationDelay: `${ci * 0.5}s`,
                  }}
                >
                  {name}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Drag hint */}
      <div
        className="border border-dashed border-border px-3 py-2.5 text-xs text-center text-muted-foreground mb-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 500ms', fontFamily: MONO }}
      >
        Drag candidate from Gallery → drop here
      </div>

      {/* Stats */}
      <div
        className="flex gap-6 pt-4 border-t border-border"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 600ms 450ms' }}
      >
        {[{ v: '7', l: 'In pipeline' }, { v: '2', l: 'In process' }, { v: '1', l: 'Hired' }].map(s => (
          <div key={s.l}>
            <p className="text-xl font-bold" style={{ fontFamily: SERIF }}>{s.v}</p>
            <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>{s.l}</p>
          </div>
        ))}
        <div className="ml-auto flex items-end">
          <span
            className="text-xs bg-green-100 border border-green-200 text-green-700 px-2 py-1"
            style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 700ms', fontFamily: MONO }}
          >
            2 new matches
          </span>
        </div>
      </div>
    </div>
  );
};

// Mock 4: Talent Briefs
const TalentBriefsMock = ({ inView }) => {
  const briefs = [
    { name: 'Sr. Womenswear — Milan', chips: ['Senior', 'Milan', 'Immediate'], count: 8,  active: true,  delay: 0 },
    { name: 'Art Director — Paris',   chips: ['Lead', 'Paris', 'Hybrid'],      count: 5,  active: true,  delay: 120 },
    { name: 'Sustainable Designer',    chips: ['Mid-level', 'Remote'],           count: 12, active: false, delay: 240 },
  ];
  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] relative overflow-hidden">
      <div
        className="flex items-center justify-between mb-4"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 80ms' }}
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>Talent Briefs — 3 active</p>
        <span className="text-xs border border-border px-2.5 py-1 text-muted-foreground" style={{ fontFamily: MONO }}>+ New Brief</span>
      </div>

      <div className="space-y-3">
        {briefs.map((b, i) => (
          <div
            key={b.name}
            className="bg-white border border-border p-4"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-12px)',
              transition: `opacity 550ms ease-out ${b.delay + 120}ms, transform 550ms ease-out ${b.delay + 120}ms`,
              borderColor: b.active && i === 0 ? 'hsl(var(--primary) / 0.35)' : '',
              background: b.active && i === 0 ? 'hsl(var(--primary) / 0.025)' : '',
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-sm font-semibold leading-tight">{b.name}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                {b.active && (
                  <span className="relative">
                    <Bell className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[hsl(var(--primary))] rounded-full" />
                  </span>
                )}
                <span className="text-xs border border-border px-1.5 py-0.5 font-medium" style={{ fontFamily: MONO }}>
                  {b.count} matches
                </span>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap mb-2.5">
              {b.chips.map(chip => (
                <span key={chip} className="text-xs border border-border px-2 py-0.5 text-muted-foreground">{chip}</span>
              ))}
            </div>
            <button className="text-xs text-[hsl(var(--primary))] flex items-center gap-1 hover:gap-2 transition-all">
              Browse Matches <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      <div
        className="absolute bottom-5 right-5"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 700ms', animation: inView ? 'floatCard 4s ease-in-out infinite' : 'none' }}
      >
        <div className="bg-foreground text-background px-3 py-2 flex items-center gap-2">
          <Bell className="w-3 h-3 text-[hsl(var(--primary))]" />
          <span className="text-xs" style={{ fontFamily: MONO }}>3 new fits found</span>
        </div>
      </div>
    </div>
  );
};

// Mock 5: Compare & Decide
const CompareMock = ({ inView }) => {
  const rows = [
    ['Rating',       '★ 9.9 / 10',          '★ 9.4 / 10'],
    ['Experience',   '8 years',              '6 years'],
    ['Location',     'Milan, Italy',          'Stockholm, Sweden'],
    ['Availability', 'Immediate',            '1 month notice'],
    ['Style DNA',    'Minimalist, Luxury',    'Avant-garde, RTW'],
    ['Style Match',  '95%',                   '78%'],
  ];
  return (
    <div className="bg-[hsl(var(--secondary))] border border-border p-5 lg:p-7 min-h-[420px] overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div />
        {[
          { label: 'Sofia Romano', sub: 'Best match', dark: true,  delay: 80  },
          { label: 'Emma Larsson', sub: 'Runner up',  dark: false, delay: 160 },
        ].map(c => (
          <div
            key={c.label}
            className={`p-3 text-center border ${c.dark ? 'bg-foreground text-background border-foreground' : 'bg-white border-border'}`}
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 550ms ease-out ${c.delay}ms, transform 550ms ease-out ${c.delay}ms`,
            }}
          >
            <div className={`w-7 h-7 mx-auto mb-1 flex items-center justify-center text-xs font-bold border ${c.dark ? 'border-background/30' : 'border-border'}`}>
              {c.label.split(' ').map(n => n[0]).join('')}
            </div>
            <p className="text-xs font-semibold truncate">{c.label}</p>
            <p className="text-xs opacity-60 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {rows.map(([label, v1, v2], ri) => (
        <div
          key={label}
          className="grid grid-cols-3 gap-2 mb-1.5"
          style={{ opacity: inView ? 1 : 0, transition: `opacity 550ms ease-out ${280 + ri * 70}ms` }}
        >
          <div className="bg-white border border-border px-2 py-1.5 text-xs text-muted-foreground uppercase tracking-wide" style={{ fontFamily: MONO, fontSize: '0.6rem' }}>{label}</div>
          <div className="border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.04)] px-2 py-1.5 text-xs font-medium">{v1}</div>
          <div className="bg-white border border-border px-2 py-1.5 text-xs text-muted-foreground">{v2}</div>
        </div>
      ))}

      {/* Outreach/Interview badges */}
      <div
        className="flex gap-2 mt-3"
        style={{ opacity: inView ? 1 : 0, transition: 'opacity 500ms 850ms' }}
      >
        <span className="text-xs border border-border px-2.5 py-1 text-muted-foreground" style={{ fontFamily: MONO }}>Generate outreach →</span>
        <span className="text-xs border border-border px-2.5 py-1 text-muted-foreground" style={{ fontFamily: MONO }}>Interview questions →</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════════════════════════════════════

// Proprietary algorithm spec chips
const ALGO_SPECS = [
  'Skills Analysis',
  'Style DNA',
  'Social Signal',
  'Logistics Fit',
  'Semantic Embeddings',
  'Cultural Vectors',
  'TF-IDF Layer',
  'Trend Manifest',
  'GPT Re-ranking',
  'Proprietary Algorithm',
  'Multi-Layer Scoring',
];

const HeroSection = ({ onGallery, onMatchmakerPopup }) => (
  <section className="relative py-24 lg:py-36 px-6 lg:px-20 bg-white border-b border-border overflow-hidden">
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.02]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }}
    />
    <div className="absolute top-0 left-0 right-0 h-[1px] bg-[hsl(var(--primary)/0.35)]" />

    <div className="max-w-[1200px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

        {/* Copy */}
        <div className="lg:col-span-6 space-y-7">
          <Reveal delay={0}>
            <MonoLabel>First HR Intelligence · Fashion Industry</MonoLabel>
          </Reveal>

          <Reveal delay={80}>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.0] tracking-[-0.03em]"
              style={{ fontFamily: SERIF }}
            >
              Elevate Your<br />Creative Guard.
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              FashionSpyder provides HR teams with a refined advantage: verified professional profiles,
              intuitive aesthetic matching, and a seamless talent pipeline.
            </p>
          </Reveal>

          {/* Proprietary algorithm spec line */}
          <Reveal delay={210}>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {ALGO_SPECS.map((spec, i) => (
                <span
                  key={spec}
                  className="text-xs border border-border/60 px-2 py-0.5 text-muted-foreground/70"
                  style={{
                    fontFamily: MONO,
                    fontSize: '0.62rem',
                    letterSpacing: '0.06em',
                    opacity: 1,
                    animation: `fadeChip 400ms ease-out both`,
                    animationDelay: `${210 + i * 40}ms`,
                  }}
                  data-testid={`algo-spec-${i}`}
                >
                  {spec}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div className="flex flex-wrap gap-3 pt-1">
              {/* Silver button */}
              <button
                onClick={onGallery}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-medium transition-all"
                style={{
                  background: 'linear-gradient(160deg, #f2f2f2 0%, #d8d8d8 100%)',
                  border: '1px solid #b0b0b0',
                  color: '#1c2128',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(160deg, #e8e8e8 0%, #ccc 100%)'; e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(160deg, #f2f2f2 0%, #d8d8d8 100%)'; e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.7)'; }}
                data-testid="hero-primary-cta-button"
              >
                Explore the Gallery <ArrowRight className="w-5 h-5" />
              </button>

              {/* Gold button */}
              <button
                onClick={onMatchmakerPopup}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-medium transition-all"
                style={{
                  background: 'hsl(43 74% 53%)',
                  border: '1px solid hsl(43 74% 42%)',
                  color: '#fff',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'hsl(43 74% 46%)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'hsl(43 74% 53%)'; }}
                data-testid="hero-secondary-cta-button"
              >
                <Sparkles className="w-4 h-4" /> Experience the Curator
              </button>
            </div>
          </Reveal>
        </div>

        {/* Hero card */}
        <Reveal delay={160} className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -top-3 right-3 w-full h-full border border-border bg-[hsl(var(--muted)/0.4)] -z-10" />
            <div className="absolute top-3 -right-3 w-full h-full border border-border bg-[hsl(var(--secondary))] z-0" />

            <div className="bg-white border border-border p-6 relative z-10 shadow-[3px_3px_0px_0px_hsl(var(--border))]">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 bg-secondary border border-border flex items-center justify-center text-sm font-bold text-muted-foreground flex-shrink-0">SR</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">Sofia Romano</p>
                  <p className="text-sm text-muted-foreground">Senior Womenswear Designer</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <MapPin className="w-3 h-3" />Milan, Italy
                  </div>
                </div>
                <div className="border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.04)] px-2.5 py-2 text-center flex-shrink-0">
                  <p className="text-2xl font-bold leading-none text-[hsl(var(--primary))]" style={{ fontFamily: SERIF }}>9.9</p>
                  <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: MONO }}>rank</p>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap mb-5">
                {['CLO 3D', 'Luxury RTW', 'Immediate', 'Remote OK'].map(tag => (
                  <span key={tag} className="text-xs border border-border px-2 py-0.5 text-muted-foreground">{tag}</span>
                ))}
              </div>

              <div className="space-y-1.5">
                {[
                  { l: 'Skills',    w: '88%', c: 'bg-blue-400' },
                  { l: 'Style DNA', w: '95%', c: 'bg-[hsl(var(--primary))]' },
                  { l: 'Social',    w: '82%', c: 'bg-green-400' },
                  { l: 'Logistics', w: '90%', c: 'bg-gray-400' },
                ].map(b => (
                  <div key={b.l} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20 flex-shrink-0" style={{ fontFamily: MONO, fontSize: '0.68rem' }}>{b.l}</span>
                    <div className="flex-1 h-1 bg-secondary overflow-hidden">
                      <div className={`h-full ${b.c}`} style={{ width: b.w }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right flex-shrink-0" style={{ fontFamily: MONO, fontSize: '0.68rem' }}>{b.w}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="absolute -bottom-4 -left-4 bg-foreground text-background px-4 py-2.5 flex items-center gap-2 z-20"
              style={{ animation: 'floatCard 3.5s ease-in-out infinite' }}
            >
              <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              <span className="text-xs uppercase tracking-wide" style={{ fontFamily: MONO }}>AI Matched · 87 pts</span>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// FASHION CAPITALS MARQUEE
// ═══════════════════════════════════════════════════════════════════════════════
const CAPITALS = [
  { city: 'Milan',      note: 'Fashion Capital'    },
  { city: 'Paris',      note: 'Haute Couture'       },
  { city: 'London',     note: 'Creative Hub'        },
  { city: 'New York',   note: 'Commercial Power'     },
  { city: 'Tokyo',      note: 'Avant-Garde'          },
  { city: 'Copenhagen', note: 'Sustainable Design'   },
  { city: 'Antwerp',    note: 'Conceptual Fashion'   },
  { city: 'Amsterdam',  note: 'Dutch Edge'           },
  { city: 'Florence',   note: 'Artisan Heritage'     },
  { city: 'Berlin',     note: 'Underground Culture'  },
  { city: 'Los Angeles',note: 'West Coast Cool'      },
  { city: 'Seoul',      note: 'K-Fashion Wave'       },
  { city: 'Shanghai',   note: 'Eastern Luxury'       },
  { city: 'Stockholm',  note: 'Scandi Minimalism'    },
  { city: 'Madrid',     note: 'Iberian Flair'        },
  { city: 'Barcelona',  note: 'Mediterranean Edge'   },
  { city: 'Mumbai',     note: 'Couture Craft'        },
  { city: 'São Paulo',  note: 'Tropical Modernism'   },
  { city: 'Dubai',      note: 'Gulf Glamour'         },
  { city: 'Lagos',      note: 'New Africa Style'     },
  { city: 'Mexico City',note: 'Latin Vanguard'       },
  { city: 'Vienna',     note: 'Old-World Tailoring'  },
];

const FashionCapitals = () => (
  <section className="py-16 px-0 border-b border-border overflow-hidden">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center mb-12" style={{ fontFamily: MONO }}>
          Talent scouted across fashion's most iconic cities
        </p>
      </Reveal>
    </div>

    <style>{`
      @keyframes fs-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      .fs-marquee-track { display:flex; width:max-content; animation: fs-marquee 60s linear infinite; }
      .fs-marquee-wrap:hover .fs-marquee-track { animation-play-state: paused; }
      @media (prefers-reduced-motion: reduce) { .fs-marquee-track { animation: none; } }
    `}</style>

    <div
      className="fs-marquee-wrap relative"
      data-testid="cities-marquee"
      style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)' }}
    >
      <div className="fs-marquee-track">
        {[...CAPITALS, ...CAPITALS].map((c, i) => (
          <div key={i} className="text-center group px-7 lg:px-9 flex-shrink-0" aria-hidden={i >= CAPITALS.length}>
            <p
              className="text-xl lg:text-2xl font-semibold tracking-[0.12em] text-foreground/25 group-hover:text-foreground/70 whitespace-nowrap"
              style={{ fontFamily: SERIF, transition: 'color 350ms ease-out' }}
            >
              {c.city.toUpperCase()}
            </p>
            <p className="text-muted-foreground/50 group-hover:text-muted-foreground uppercase tracking-widest mt-0.5 whitespace-nowrap" style={{ fontFamily: MONO, fontSize: '0.58rem', transition: 'color 350ms ease-out' }}>
              {c.note}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURE SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const FeatureSection = ({ label, headline, body, bullets, cta, MockComponent, reversed = false, bg = 'bg-white' }) => {
  const [sectionRef, inView] = useInView({ threshold: 0.08 });

  return (
    <section ref={sectionRef} className={`${bg} py-20 lg:py-28 px-6 lg:px-20`}>
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">

          {/* Text side */}
          <div
            className={`lg:col-span-5 space-y-7 ${reversed ? 'lg:order-2' : 'lg:order-1'}`}
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(18px)',
              transition: 'opacity 700ms ease-out 60ms, transform 700ms ease-out 60ms',
            }}
          >
            <MonoLabel>{label}</MonoLabel>

            <h2 className="text-3xl lg:text-4xl font-semibold tracking-[-0.025em] leading-[1.1]" style={{ fontFamily: SERIF }}>
              {headline}
            </h2>

            {/* Optional body paragraph — no bullet/check */}
            {body && (
              <p className="text-base text-muted-foreground leading-relaxed border-l-2 border-[hsl(var(--primary)/0.4)] pl-4 italic">
                {body}
              </p>
            )}

            {bullets && (
              <ul className="space-y-3.5">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
                    <Check className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ opacity: inView ? 1 : 0, transition: 'opacity 700ms ease-out 300ms' }}>
              <button
                onClick={cta.onClick}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-foreground/70 text-foreground hover:bg-foreground hover:text-background transition-all"
                data-testid={cta.testid}
                style={{ letterSpacing: '0.01em' }}
              >
                {cta.label} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mock side */}
          <div
            className={`lg:col-span-7 ${reversed ? 'lg:order-1' : 'lg:order-2'}`}
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 700ms ease-out 180ms, transform 700ms ease-out 180ms',
            }}
          >
            <MockComponent inView={inView} />
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ROLES MARQUEE
// ═══════════════════════════════════════════════════════════════════════════════
const ROLES = [
  { name: 'Womenswear Designer',  img: 'https://images.pexels.com/photos/9849319/pexels-photo-9849319.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Fashion Photographer', img: 'https://images.pexels.com/photos/25312270/pexels-photo-25312270.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Fashion Stylist',      img: 'https://images.pexels.com/photos/8089252/pexels-photo-8089252.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Art Director',         img: 'https://images.pexels.com/photos/9492917/pexels-photo-9492917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Makeup Artist',        img: 'https://images.pexels.com/photos/4287637/pexels-photo-4287637.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Pattern Maker',        img: 'https://images.pexels.com/photos/6461086/pexels-photo-6461086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Knitwear Designer',    img: 'https://images.pexels.com/photos/6660920/pexels-photo-6660920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Creative Director',    img: 'https://images.pexels.com/photos/6850547/pexels-photo-6850547.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Trend Forecaster',     img: 'https://images.pexels.com/photos/18913155/pexels-photo-18913155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
  { name: 'Visual Merchandiser',  img: 'https://images.pexels.com/photos/6894175/pexels-photo-6894175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=380' },
];

const RolesMarquee = ({ onNavigate }) => {
  const [paused, setPaused] = useState(false);
  const doubled = [...ROLES, ...ROLES];

  return (
    <div className="overflow-hidden border-y border-border bg-white py-10">
      <div
        className="flex gap-5"
        style={{
          animation: 'marquee 45s linear infinite',
          animationPlayState: paused ? 'paused' : 'running',
          willChange: 'transform',
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {doubled.map((role, i) => (
          <div
            key={`${role.name}-${i}`}
            onClick={onNavigate}
            className="flex-shrink-0 w-[190px] cursor-pointer group border border-border bg-white"
            data-testid={`roles-carousel-card-${role.name.toLowerCase().replace(/\s+/g, '-')}-${i}`}
            style={{ transition: 'border-color 300ms ease-out' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'hsl(var(--foreground) / 0.3)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'hsl(var(--border))'}
          >
            <div className="relative overflow-hidden bg-secondary" style={{ aspectRatio: '3/4' }}>
              <img
                src={role.img}
                alt={role.name}
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ filter: 'grayscale(1) contrast(1.05)', transition: 'filter 500ms ease-out' }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0.2) contrast(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(1) contrast(1.05)'; }}
              />
            </div>
            <div className="p-3 border-t border-border">
              <p className="text-sm font-semibold text-foreground leading-tight">{role.name}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FINAL CTA
// ═══════════════════════════════════════════════════════════════════════════════
const FinalCTA = ({ onGallery, onPricing }) => (
  <section className="py-28 px-6 lg:px-20 bg-white border-t border-border">
    <div className="max-w-[800px] mx-auto">
      <Reveal>
        <div className="w-10 h-[2px] bg-[hsl(var(--primary))] mb-12" />
      </Reveal>
      <Reveal delay={60}>
        <h2
          className="text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.04] mb-8"
          style={{ fontFamily: SERIF }}
        >
          Ready to secure exceptional talent?
        </h2>
      </Reveal>
      <Reveal delay={130}>
        <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-lg">
          Start with the RECON plan to browse our global Gallery, or unlock full INTELLIGENCE for advanced matching and the complete AI pipeline.
        </p>
      </Reveal>
      <Reveal delay={200}>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={onGallery}
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium transition-all"
            style={{ background: '#1c2128', color: '#fff', border: '1px solid #1c2128' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#2c3138'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1c2128'; }}
            data-testid="final-cta-primary"
          >
            Start with Gallery <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={onPricing}
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium border border-border text-foreground transition-all hover:border-foreground"
            data-testid="final-cta-secondary"
          >
            View Pricing Plans
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOMEPAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Homepage = () => {
  const navigate = useNavigate();
  const [showMatchmakerPopup, setShowMatchmakerPopup] = useState(false);
  const [showWorkflowPopup, setShowWorkflowPopup] = useState(false);

  // Feature sections config
  const FEATURES = [
    {
      label: 'The Global Scouting Network',
      headline: "Verified Talent from the World's Fashion Capitals.",
      body: null,
      bullets: [
        'Access an elite network of professionals across the capitals of the industry.',
        '50+ specialist roles: from Womenswear Designers to Art Directors, Photographers to Buyers.',
        'Portfolio previews on hover. Drag candidates directly into your pipeline stages.',
        'AI Talent Brief: type a natural language description → AI ranks the gallery instantly.',
      ],
      cta: { label: 'Get Access to the Gallery', testid: 'feature-spy-gallery-cta', onClick: () => navigate('/gallery') },
      Mock: GalleryMock,
      bg: 'bg-white',
      reversed: false,
    },
    {
      label: 'The Digital Curator',
      headline: 'Intelligence that understands the language of fashion.',
      body: "Our technology doesn't just scan data; it interprets style. By translating job descriptions into structured requirements, we identify the perfect creative fit.",
      bullets: [
        'Technical Artistry: Expert proficiency in tools like CLO 3D, Clo Modaris, and Lectra.',
        'Style DNA: Precise alignment with your brand\'s aesthetic — from Minimalist to Avant-Garde.',
        'Industry Influence: Verified social proof, authority scores, and professional standing.',
        'Logistical Harmony: Real-time availability, location match, and work preferences.',
      ],
      cta: { label: 'Try the Matchmaker', testid: 'feature-ai-matchmaker-cta', onClick: () => setShowMatchmakerPopup(true) },
      Mock: MatchmakerMock,
      bg: 'bg-[hsl(var(--secondary))]',
      reversed: true,
    },
    {
      label: 'Seamless Workflow Integration',
      headline: 'Manage your entire talent pipeline in one place.',
      body: null,
      bullets: [
        'Kanban pipeline: move candidates from Saved → Contacted → In Process → Hired with drag and drop.',
        'Drag candidates from the Gallery and drop them directly into a pipeline stage.',
        'In-app notifications when shortlisted candidates update profiles or availability changes.',
        'Full match history — every AI search saved automatically with results archived.',
      ],
      cta: { label: 'See Workflow', testid: 'feature-pipeline-cta', onClick: () => setShowWorkflowPopup(true) },
      Mock: PipelineMock,
      bg: 'bg-white',
      reversed: false,
    },
    {
      label: 'Talent Briefs',
      headline: 'Save your ideal profile, let us find the matches.',
      body: null,
      bullets: [
        'Create Talent Briefs from any search — save your ideal candidate profile as a standing brief.',
        'Automatic matching: get notified when a new candidate matches your saved criteria.',
        '"Browse Matches" jumps straight to the Gallery pre-filtered to your brief.',
        'Manage multiple briefs simultaneously — ideal for ongoing multi-role hiring cycles.',
      ],
      cta: { label: 'See Talent Briefs', testid: 'feature-talent-briefs-cta', onClick: () => navigate('/gallery') },
      Mock: TalentBriefsMock,
      bg: 'bg-[hsl(var(--secondary))]',
      reversed: true,
    },
    {
      label: 'Compare & Decide',
      headline: 'Make smarter hiring decisions, faster.',
      body: null,
      bullets: [
        'Side-by-side analysis of top talent across all V-dimensions: Skills, Style, Social, Logistics.',
        'Generate personalised interview questions and outreach messages in one click.',
        '"Find Similar" — use any candidate as a reference to discover profiles with matching DNA.',
        'Continuously growing: fresh applicants as our partner community grows the directory.',
      ],
      cta: { label: 'See Comparisons', testid: 'feature-compare-cta', onClick: () => navigate('/gallery') },
      Mock: CompareMock,
      bg: 'bg-white',
      reversed: false,
    },
    {
      label: 'AI Analyst — Intelligence',
      headline: 'Interrogate every candidate, instantly.',
      body: "Every profile opens with a sharp AI snapshot — then ask anything. Your private analyst answers in seconds, grounded in every signal we hold.",
      bullets: [
        'Two-sentence intelligence snapshot auto-generated on every profile.',
        'Conversational Q&A — fit, seniority, red flags, aesthetic match — like a recruiter on call.',
        'Answers are grounded in scores, Style DNA, CV and cultural signals.',
        'Contact details always protected — every conversation stays on-platform.',
      ],
      cta: { label: 'Explore the Gallery', testid: 'ai-analyst-demo-cta', onClick: () => navigate('/gallery') },
      Mock: AIAnalystMock,
      bg: 'bg-[hsl(var(--secondary))]',
      reversed: true,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AI Talent Intelligence for Fashion Brands"
        description="FashionSpyder is the first AI-powered HR intelligence platform built exclusively for the fashion industry. Access verified fashion professionals from Milan, Paris, London, Tokyo and beyond."
        path="/"
        schema={[schemaOrganization, schemaWebSite, schemaSoftwareApp]}
      />
      {showMatchmakerPopup && (
        <MatchmakerPopup
          onClose={() => setShowMatchmakerPopup(false)}
          onTry={() => { setShowMatchmakerPopup(false); navigate('/ai-matchmaker'); }}
        />
      )}

      {showWorkflowPopup && (
        <WorkflowPopup
          onClose={() => setShowWorkflowPopup(false)}
          onTry={() => { setShowWorkflowPopup(false); navigate('/dashboard/pipeline'); }}
        />
      )}

      <HeroSection
        onGallery={() => navigate('/gallery')}
        onMatchmakerPopup={() => setShowMatchmakerPopup(true)}
      />

      <FashionCapitals />

      {FEATURES.map(feat => (
        <FeatureSection
          key={feat.label}
          label={feat.label}
          headline={feat.headline}
          body={feat.body}
          bullets={feat.bullets}
          cta={feat.cta}
          MockComponent={feat.Mock}
          reversed={feat.reversed}
          bg={feat.bg}
        />
      ))}

      {/* Roles section */}
      <section className="pt-20 pb-0 px-6 lg:px-20">
        <div className="max-w-[1200px] mx-auto mb-10">
          <Reveal>
            <MonoLabel>Explore by Role</MonoLabel>
          </Reveal>
          <Reveal delay={60}>
            <h2
              className="text-4xl lg:text-5xl font-semibold tracking-tight mt-5 mb-2"
              style={{ fontFamily: SERIF }}
            >
              Every fashion role,<br />one platform.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-lg text-muted-foreground">
              From Creative Directors to Pattern Makers — every specialist, in one curated directory.
            </p>
          </Reveal>
        </div>
      </section>

      <RolesMarquee onNavigate={() => navigate('/gallery')} />

      <FinalCTA
        onGallery={() => navigate('/gallery')}
        onPricing={() => navigate('/pricing')}
      />
    </div>
  );
};

export default Homepage;
